import type { PressEvent } from '@react-types/shared';

import {
  Button,
  ButtonProps,
  CSS,
  Flex,
  Icon,
  IconButton,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

type Props = {
  icon?: string | (() => JSX.Element);
  iconColor?: string;
  title: string;
  subTitle?: string | JSX.Element;
  buttonText?: string;
  css?: CSS;
  buttonProps?: ButtonProps;
  secondaryButtonProps?: ButtonProps;
  tertiaryButtonProps?: ButtonProps;

  onClose?: (event: PressEvent) => void;
  titleCSS?: CSS;
  secondaryButtonText?: string;
  tertiaryButtonText?: string;
};

export function Banner({
  icon,
  iconColor,
  title,
  subTitle,
  buttonText,
  buttonProps,
  secondaryButtonProps,
  tertiaryButtonProps,
  onClose,
  css,
  titleCSS,
  secondaryButtonText,
  tertiaryButtonText,
}: Props): JSX.Element {
  const { t } = useTranslation();

  let iconElement: JSX.Element | null;

  switch (typeof icon) {
    case 'string':
      iconElement = (
        <Icon icon={icon} css={{ color: iconColor }} data-testid="icon" />
      );
      break;
    case 'function':
      iconElement = icon();
      break;
    default:
      iconElement = null;
  }

  return (
    <Flex
      align="center"
      gap="medium"
      css={{
        padding: '$small $large',
        ...css,
      }}
      role="banner"
    >
      {iconElement}
      <Typography css={{ ...titleCSS }}>
        <Typography
          variant="bodyBold"
          css={{ mr: subTitle && '$x-small', ...titleCSS }}
        >
          {title}
        </Typography>
        {subTitle}
      </Typography>
      {buttonText && buttonProps?.onPress && (
        <Button
          variant="primary"
          size={buttonProps.size}
          onPress={buttonProps.onPress}
          data-testid="action-button"
          css={{ ...buttonProps.css }}
        >
          {buttonText}
        </Button>
      )}
      {secondaryButtonText && secondaryButtonProps?.onPress && (
        <Button
          variant="secondary"
          size={secondaryButtonProps.size}
          onPress={secondaryButtonProps.onPress}
          data-testid="action-button-secondary"
          endIcon={secondaryButtonProps.endIcon}
          css={{ ...secondaryButtonProps.css }}
        >
          {secondaryButtonText}
        </Button>
      )}
      {tertiaryButtonText && tertiaryButtonProps?.onPress && (
        <Button
          variant="tertiary"
          size={tertiaryButtonProps.size}
          onPress={tertiaryButtonProps.onPress}
          data-testid="action-button-tertiary"
          endIcon={tertiaryButtonProps.endIcon}
          css={{ ...tertiaryButtonProps.css }}
        >
          {tertiaryButtonText}
        </Button>
      )}
      {onClose && (
        <IconButton
          variant="tertiary"
          onPress={onClose}
          title={t('global.close')}
          data-testid="close-button"
        >
          <Icon icon="close" />
        </IconButton>
      )}
    </Flex>
  );
}
